import type { HTMLAttributes } from "react";

export interface NewBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * Optional date stamp shown after "new ·" — typically an ISO date or
   * short label ("2026-05-19"). Omit it for a bare "new" pill (the compact
   * form, e.g. a sidebar nav item where horizontal space is tight).
   */
  date?: string;
  /**
   * Optional label shown after "new ·" when the pill names WHAT is new rather
   * than when ("new · Bullseye" on the home page's "What's new" slides).
   * Same rendering as `date`; a separate prop so a call site says which one
   * it means. Wins over `date` if both are passed.
   */
  label?: string;
}

/**
 * NewBadge — the v18-cycle pastel-green "new" pill used to surface
 * freshly-added UI surfaces. Renders a bare "new" by default, or
 * "new · {date}" when a `date` stamp is passed (e.g. the iteration date).
 * The bare form keeps tight surfaces (sidebar nav rows) compact. Replaces
 * the older `.badge-new` rule that had zero usages.
 *
 * v18 spec class `.badge-new-date`: pastel-green pastel background with
 * a hairline border, 600-weight 10px text, slight letter-spacing. Mirrors
 * `ContextBadge` for the pill shape but uses the success-tinted palette
 * to signal "new" rather than "context".
 */
export function NewBadge({ date, label, className, ...rest }: NewBadgeProps) {
  // Dedicated tokens, like `UpdatedBadge` (`bg-info-muted border-info-strong
  // text-info`). It was `bg-success/15 border-success/25 text-success`: the
  // `/<alpha>` modifier emits nothing on a hex CSS-var colour (tokens.css), so
  // the pill rendered with no fill and a grey hairline. `-ink` for the TEXT.
  const base =
    "inline-flex items-center gap-1 rounded-full bg-success-muted border border-success-strong px-2 py-0.5 text-micro tracking-[0.02em] text-success-ink";
  const classes = [base, className].filter(Boolean).join(" ");
  return (
    <span {...rest} className={classes}>
      {label || date ? `new · ${label || date}` : "new"}
    </span>
  );
}
